// frontend/src/pages/admin/AdminBrandingPage.tsx

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Image as ImageIcon, Type, Save, Loader, Zap, Upload, Mail, Users, ArrowLeft, Info, RotateCcw, Lock } from "lucide-react";
import * as api from "../../services/api";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useBranding } from "../../contexts/BrandingContext";
import { resolveHomeSubtitle } from "../../utils/resolveHomeSubtitle";
import { ImageCropper } from "../../components/admin/ImageCropper";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import styles from "./AdminBrandingPage.module.css";

export default function AdminBrandingPage() {
  const { t } = useTranslation(); // 🆕
  const navigate = useNavigate();
  const { refreshBranding } = useBranding();
  const { artist, organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: undefined as (() => void) | undefined,
    icon: "success" as "success" | "info" | "danger",
    variant: "primary" as "primary" | "danger",
  });
  
  const [formData, setFormData] = useState({
    logoUrl: "",
    homeTitle: "Yulqo",
    homeSubtitle: "Sin colas. Solo tu turno.",
    primaryColor: "#000000",
    secondaryColor: "#FFFFFF",
    address: "",
    phone: "",
    email: "",
    legalMentions: "",
    invoiceFooter: "",
    invoiceServiceName: ""
  });

  const artistId = artist?.id || "1"; 

  const { data: billingStatus } = useQuery({
    queryKey: ["billingStatus"],
    queryFn: () => api.getBillingStatus(),
  });
  const isFreePlan = (billingStatus?.plan ?? "FREE") !== "PRO";

  const getLegalPlaceholder = () => t("admin.branding.legalPlaceholder");

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setTempImage(reader.result as string);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    
    // Reset input so the same file can be chosen again
    e.target.value = "";
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setCropModalOpen(false);
    setUploading(true);
    try {
      // Convert Blob to File for the API
      const file = new File([croppedBlob], "logo.png", { type: "image/png" });
      
      console.log("📤 Début upload logo recadré pour l'artiste:", artistId);
      const logoUrl = await api.uploadLogo(artistId, file);
      console.log("✅ Logo uploadé avec succès, URL:", logoUrl);
      setFormData((prev) => ({ ...prev, logoUrl }));
    } catch (error: any) {
      console.error("❌ Erreur upload logo:", error.response?.data || error.message);
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: t("common.error") || "Erreur lors de l'upload du logo",
        onConfirm: undefined,
        icon: "danger",
        variant: "primary",
      });
    } finally {
      setUploading(false);
      setTempImage(null);
    }
  };
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const data = await api.getBranding(artistId);
        if (data && data.id) {
          setFormData({
            logoUrl: data.logoUrl || "",
            homeTitle: data.homeTitle || "Yulqo",
            homeSubtitle: resolveHomeSubtitle(
              data.homeSubtitle,
              organization?.businessType ?? "OTHER",
              data.homeTitle || organization?.name || "",
              t,
              t("look.lead"),
            ),
            primaryColor: data.primaryColor || "#000000",
            secondaryColor: data.secondaryColor || "#FFFFFF",
            address: data.address || "",
            phone: data.phone || "",
            email: data.email || "",
            legalMentions: data.legalMentions || "",
            invoiceFooter: data.invoiceFooter || "",
            invoiceServiceName: data.invoiceServiceName || ""
          });
        }
      } catch (error) {
        console.error("Error fetching branding:", error);
      } finally {
        setLoading(false);
      }
    };
    if (artistId && artistId !== "1") {
      fetchBranding();
    }
  }, [artistId, organization?.businessType, organization?.name, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isInvalidArtist = !artistId || artistId === "1";
    if (isInvalidArtist) {
       console.error("❌ ArtistId invalide pour la sauvegarde");
       return;
    }

    setSaving(true);
    try {
      // Nettoyer les données pour éviter d'envoyer des champs système
      const { id: _id, artistId: _aid, createdAt: _ca, updatedAt: _ua, ...cleanData } = formData as any;
      
      console.log("💾 Sauvegarde du branding pour:", artistId, cleanData);
      await api.updateBranding(artistId, cleanData);
      console.log("✅ Branding sauvegardé");
      refreshBranding();
      setModalState({
        isOpen: true,
        title: `✅ ${t("common.success")}`,
        message: t("admin.branding.success") || "Modifications enregistrées !",
        onConfirm: undefined,
        icon: "success",
        variant: "primary",
      });
    } catch (error: any) {
      console.error("❌ Erreur sauvegarde branding:", error.response?.data || error.message);
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: error.response?.data?.error || t("common.error") || "Erreur lors de la sauvegarde",
        onConfirm: undefined,
        icon: "danger",
        variant: "primary",
      });
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false, onConfirm: undefined }));
  };

  const handleRestartOnboarding = () => {
    const isInvalidArtist = !artistId || artistId === "1";
    if (isInvalidArtist) return;

    setModalState({
      isOpen: true,
      title: t("admin.branding.restartOnboardingTitle"),
      message: t("admin.branding.restartOnboardingConfirm"),
      icon: "info",
      variant: "primary",
      onConfirm: async () => {
        closeModal();
        setRestarting(true);
        try {
          await api.updateBranding(artistId, {
            onboardingCompleted: false,
            eventTutorialCompleted: false,
          });
          await refreshBranding();
          navigate("/admin/onboarding");
        } catch (error: any) {
          console.error("❌ Erreur reset onboarding:", error.response?.data || error.message);
          setModalState({
            isOpen: true,
            title: `❌ ${t("common.error")}`,
            message: error.response?.data?.error || t("common.error"),
            onConfirm: undefined,
            icon: "danger",
            variant: "primary",
          });
        } finally {
          setRestarting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-6 h-6 animate-spin text-gray-500" />
        <span className="ml-2">{t("common.loading")}</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <Link 
          to="/admin/dashboard" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.backToDashboard")}
        </Link>
      </div>

      <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mb-8">{t("admin.branding.title")}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 border border-gray-200">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <ImageIcon className="w-4 h-4 inline mr-2" /> {t("admin.branding.logo")}
            </label>
            <div className={`relative ${isFreePlan ? "opacity-60 pointer-events-none" : ""}`}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.logoUrl}
                  onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  className="flex-1 px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
                />
                <label className="cursor-pointer bg-white text-gray-700 px-4 py-2 border border-gray-200 hover:bg-gray-50 flex items-center justify-center min-w-[50px]">
                  {uploading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5" />
                  )}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading || isFreePlan}
                  />
                </label>
              </div>
              {isFreePlan && (
                <div className="absolute inset-0 flex items-center justify-center rounded-xl">
                  <Link to="/admin/billing" className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-xs font-medium px-3 py-1.5">
                    <Lock className="w-3 h-3" /> Pro
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Type className="w-4 h-4 inline mr-2" /> {t("admin.branding.homeTitle")}
            </label>
            <input
              type="text"
              value={formData.homeTitle}
              onChange={(e) => setFormData({ ...formData, homeTitle: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
               {t("admin.branding.homeSubtitle")}
            </label>
            <textarea
              value={formData.homeSubtitle}
              onChange={(e) => setFormData({ ...formData, homeSubtitle: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className={`relative ${isFreePlan ? "opacity-60 pointer-events-none" : ""}`}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.primary")}</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  className="flex-1 px-3 border border-gray-200 text-xs"
                />
              </div>
            </div>
            <div className={`relative ${isFreePlan ? "opacity-60 pointer-events-none" : ""}`}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.secondary")}</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.secondaryColor}
                  onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.secondaryColor}
                  onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                  className="flex-1 px-3 border border-gray-200 text-xs"
                />
              </div>
            </div>
            {isFreePlan && (
              <div className="col-span-2 flex items-center gap-2 border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700">
                <Lock className="w-4 h-4 shrink-0" />
                <span>{t("admin.branding.colorsProOnly", "Couleurs personnalisées réservées au plan Pro.")}</span>
                <Link to="/admin/billing" className="ml-auto font-medium underline hover:text-gray-900 whitespace-nowrap">
                  {t("admin.billing.upgradeToPro", "Passer à Pro")}
                </Link>
              </div>
            )}
          </div>

          <hr className="border-gray-100" />
          <h3 className="text-md font-bold text-gray-800">{t("admin.branding.billingDetails")}</h3>

          <div className="border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
            <div className="flex gap-3">
              <Info className="w-5 h-5 shrink-0 mt-0.5 text-gray-500" aria-hidden="true" />
              <div className="space-y-2">
                <p className="font-semibold">{t("admin.branding.receiptInfoTitle")}</p>
                <p className="text-gray-600 leading-relaxed">{t("admin.branding.receiptInfoIntro")}</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600">
                  <li>{t("admin.branding.receiptInfoBullet1")}</li>
                  <li>{t("admin.branding.receiptInfoBullet2")}</li>
                  <li>{t("admin.branding.receiptInfoBullet3")}</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.address")}</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.phone")}</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.email")}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.serviceName")}</label>
            <input
              type="text"
              value={formData.invoiceServiceName}
              onChange={(e) => setFormData({ ...formData, invoiceServiceName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
              placeholder={t("admin.branding.serviceNamePlaceholder")}
            />
            <p className="text-xs text-gray-500 mt-1">{t("admin.branding.serviceNameHelper")}</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.legal")}</label>
            <textarea
              value={formData.legalMentions}
              onChange={(e) => setFormData({ ...formData, legalMentions: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none text-xs"
              placeholder={getLegalPlaceholder()}
              rows={2}
            />
            <p className="text-xs text-gray-500 mt-1">{t("admin.branding.legalHelper")}</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.branding.footer")}</label>
            <input
              type="text"
              value={formData.invoiceFooter}
              onChange={(e) => setFormData({ ...formData, invoiceFooter: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 focus:border-gray-900 outline-none"
              placeholder={t("admin.branding.footerPlaceholder")}
            />
          </div>


          <button
            type="submit"
            disabled={saving || !artistId || artistId === "1"}
            className="w-full py-3 bg-stone-900 text-white font-medium hover:bg-stone-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? t("common.loading") : t("common.save")}
          </button>

          <div className={styles.restartSection}>
            <h2 className={styles.restartTitle}>{t("admin.branding.restartOnboardingTitle")}</h2>
            <p className={styles.restartHint}>{t("admin.branding.restartOnboardingHint")}</p>
            <button
              type="button"
              onClick={handleRestartOnboarding}
              disabled={restarting || !artistId || artistId === "1"}
              className={styles.restartButton}
            >
              {restarting ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {t("admin.branding.restartOnboarding")}
            </button>
          </div>

        </form>

        {/* Aperçu amélioré */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-600 px-2">{t("admin.branding.preview")}</h2>
          <div className="border-[10px] border-gray-900 rounded-[3rem] overflow-hidden aspect-[9/19] bg-slate-50 shadow-2xl relative">
             {/* Encoche téléphone */}
             <div className="absolute top-0 inset-x-0 h-6 bg-gray-900 flex justify-center items-end pb-1.5 z-10">
                <div className="w-20 h-4 bg-black rounded-full"></div>
             </div>
             
             {/* Contenu simulé JoinQueuePage */}
             <div className="h-full flex flex-col items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-[240px] flex flex-col items-center">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-3 shadow-inner overflow-hidden ${formData.logoUrl ? styles.logoTransparent : styles.logoPlaceholder}`}>
                    {formData.logoUrl ? (
                       <img src={api.getAssetUrl(formData.logoUrl)} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                       <Zap className="w-6 h-6 text-gray-300 fill-current" />
                    )}
                  </div>
                  
                  <h1 className="text-lg font-bold mb-1 line-clamp-1" style={{ color: formData.primaryColor }}>
                    {formData.homeTitle}
                  </h1>
                  <p className="text-gray-500 text-[10px] mb-4 line-clamp-2">{formData.homeSubtitle}</p>
                  
                  <div className="w-full space-y-2">
                     <div className="flex flex-col gap-1">
                        <div className="h-2 w-12 bg-gray-100 rounded"></div>
                        <div className="h-8 w-full bg-gray-50 rounded-lg border border-gray-100 flex items-center px-2">
                          <Users className="w-3 h-3 text-gray-300" />
                        </div>
                     </div>
                     <div className="flex flex-col gap-1">
                        <div className="h-2 w-12 bg-gray-100 rounded"></div>
                        <div className="h-8 w-full bg-gray-50 rounded-lg border border-gray-100 flex items-center px-2">
                          <Mail className="w-3 h-3 text-gray-300" />
                        </div>
                     </div>
                     <div className="h-8 w-full rounded-lg shadow-md flex items-center justify-center" 
                          style={{ backgroundColor: formData.primaryColor }}>
                        <div className="h-2 w-16 bg-white/30 rounded"></div>
                     </div>
                     
                     <div className="flex items-center justify-center gap-1 mt-2">
                        <ArrowLeft className="w-2.5 h-2.5 text-gray-400" />
                        <div className="h-2 w-8 bg-gray-200 rounded"></div>
                     </div>
                  </div>
                </div>
                
                {/* Footer simulé */}
                <div className="absolute bottom-6 w-full px-8 text-center">
                   <p className="text-[8px] text-gray-400 leading-tight">
                    {formData.legalMentions || "SIRET, TVA..."}
                   </p>
                </div>
             </div>
          </div>
        </div>
      </div>
      {cropModalOpen && tempImage && (
        <ImageCropper
          image={tempImage}
          onCropComplete={handleCropComplete}
          onClose={() => {
            setCropModalOpen(false);
            setTempImage(null);
          }}
        />
      )}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={modalState.onConfirm}
        title={modalState.title}
        message={modalState.message}
        icon={modalState.icon}
        variant={modalState.variant}
      />
    </div>
  );
}
